import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaTrashAlt } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

/**
 * Compact and clean WishlistDrawer
 * - shows thumbnail (left), title + short desc (center), price+delete (right)
 * - uses dedicated .wishlist-drawer classes to avoid interfering with cart
 */
const WishlistDrawer = ({ show, onClose, onWishlistChange }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const KEY = "wishlist";

  const readWishlist = () => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      console.error("readWishlist failed", e);
      return [];
    }
  };

  const writeWishlist = (arr) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(arr));
      if (onWishlistChange) onWishlistChange(arr.length);
    } catch (e) {
      console.error("writeWishlist failed", e);
    }
  };

  const sync = () => {
    const c = readWishlist();
    setItems(c);
    if (onWishlistChange) onWishlistChange(c.length);
  };

  useEffect(() => {
    sync();
    const onStorage = (e) => {
      if (!e || !e.key) {
        sync();
        return;
      }
      if (e.key === KEY) sync();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (show) sync();
  }, [show]);

  const remove = (productId, categorySizeId) => {
    const current = readWishlist();
    const filtered = current.filter(
      (it) =>
        !(
          String(it.productId) === String(productId) &&
          String(it.categorySizeId) === String(categorySizeId)
        )
    );
    writeWishlist(filtered);
    setItems(filtered);
  };

  const goToProduct = (item) => {
    // prefer category route otherwise fallback to product route
    if (item.categoryId && item.categoryName) {
      navigate(`/product/${item.productId}/category/${item.categoryId}`);
    } else if (item.productId) {
      navigate(`/home/product/${item.productId}`);
    }
    onClose?.();
  };

  const shortDesc = (text = "", words = 5) => {
    const t = String(text || "").trim();
    if (!t) return "";
    const arr = t.split(/\s+/);
    if (arr.length <= words) return t;
    return arr.slice(0, words).join(" ") + "...";
  };

  return (
    <>
      <div
        className={`wishlist-backdrop ${show ? "visible" : ""}`}
        onClick={onClose}
      />
      <aside
        className={`wishlist-drawer ${show ? "open" : ""}`}
        aria-hidden={!show}
      >
        <div className="wishlist-header">
          <h5 className="mb-0">My Wishlist ({items.length})</h5>
          <button
            className="btn btn-link p-0 wishlist-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <IoClose size={22} />
          </button>
        </div>

        <div className="wishlist-body">
          {items.length === 0 ? (
            <div className="wishlist-empty">
              <p>
                <strong>Your wishlist is empty</strong>
              </p>
              <Link to="/home" className="small text-muted">
                Browse products
              </Link>
            </div>
          ) : (
            items.map((item, idx) => {
              const img = item.productImage
                ? `https://goldenhides.in/api/product/${item.productImage}`
                : null;
              return (
                <div className="wishlist-item" key={idx}>
                  <div
                    className="wishlist-thumb"
                    onClick={() => goToProduct(item)}
                    style={{
                      width: 110,
                      height: 110,
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "#f8f8f8",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.productImage ? (
                      <img
                        src={`https://goldenhides.in/api/product/${item.productImage}`}
                        alt={item.productName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          background: "#f1f1f1",
                        }}
                      />
                    )}
                  </div>

                  <div
                    className="wishlist-info"
                    onClick={() => goToProduct(item)}
                  >
                    <div className="wishlist-title">
                      {item.productName || "-"}
                    </div>
                    {item.description ? (
                      <div className="wishlist-desc">
                        {shortDesc(item.description, 5)}
                      </div>
                    ) : null}
                  </div>

                  <div className="wishlist-actions">
                    <div className="wishlist-price">
                      {item.price !== undefined && item.price !== null
                        ? `₹ ${Number(item.price).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                          })}`
                        : ""}
                    </div>
                    <button
                      className="btn btn-link wishlist-remove"
                      onClick={() =>
                        remove(item.productId, item.categorySizeId)
                      }
                      aria-label="Remove"
                      title="Remove"
                    >
                      <FaTrashAlt size={16} color="#e53935" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="wishlist-footer">
          <div className="small text-muted text-center">
            Login to save wishlist across devices
          </div>
        </div>
      </aside>
    </>
  );
};

export default WishlistDrawer;
